// 261005 E1-P1 — dữ liệu dùng chung của mọi chặng: khung xương (record), kịch bản (scripts), chương thô (rawChapters).
// Dời từ `AdaptationTab.load()` (:102-120) và `AdaptationSource.load()` (:70). Chặng nhận qua context, KHÔNG fetch riêng.
// `localizeView` cố ý KHÔNG nằm ở đây: AdaptationLocalize tự fetch và chỉ mount ở chặng ③.
import { useCallback, useEffect, useRef, useState } from 'react';
import { adaptationApi } from '../../../services/api/adaptation';
import type { EpisodeScriptRecord, OutlineRecord, RawChapter } from '../../../types/adaptation';
import { isLoadFailure } from '../sourceChapterUtils';

export interface AdaptationData {
  record: OutlineRecord | null;
  scripts: EpisodeScriptRecord[];
  rawChapters: RawChapter[];
  /** Mới chỉ `true` trước lần tải đầu của truyện hiện tại (để vùng chặng spin, khung vẫn hiện). */
  loading: boolean;
  /** F20 — lần tải gần nhất KHÔNG được (mất kết nối / 5xx). Khi `true`, dữ liệu đang giữ là bản cũ / rỗng — KHÔNG được hiện như "truyện trống". */
  loadError: boolean;
  /** Nạp lại cả ba. Trả record mới (để `patch` đọc được số liệu ngay, không đợi re-render). */
  reload: () => Promise<OutlineRecord | null>;
  /** Chỉ nạp lại danh sách kịch bản (sau sửa tập). */
  reloadScripts: () => Promise<void>;
}

export const useAdaptationData = (storyId: string): AdaptationData => {
  const [record, setRecord] = useState<OutlineRecord | null>(null);
  const [scripts, setScripts] = useState<EpisodeScriptRecord[]>([]);
  const [rawChapters, setRawChapters] = useState<RawChapter[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const currentStory = useRef(storyId);
  /** Lượt GET cũ trả muộn không được phép ghi đè state vừa nạp sau một mutation. */
  const latestLoad = useRef(0);
  currentStory.current = storyId;

  const reload = useCallback(async (): Promise<OutlineRecord | null> => {
    const asked = storyId;
    const loadNo = ++latestLoad.current;
    // 404 / chưa có (chưa dựng khung xương, chưa có kịch bản) = rỗng thật; mất kết nối / 5xx = lỗi tải (F20) ⇒ giữ dữ liệu đang có, bật `loadError`.
    const call = <T,>(p: Promise<T>, empty: T): Promise<{ v: T; failed: boolean }> =>
      p.then((v) => ({ v, failed: false })).catch((e) => ({ v: empty, failed: isLoadFailure(e) }));
    const [rec, scr, raws] = await Promise.all([
      call<OutlineRecord | null>(adaptationApi.getOutline(storyId), null),
      call<EpisodeScriptRecord[]>(adaptationApi.scripts(storyId), []),
      call<RawChapter[]>(adaptationApi.rawChapters(storyId), []),
    ]);
    if (currentStory.current !== asked || latestLoad.current !== loadNo) return null; // đổi truyện / GET cũ trả muộn
    if (!rec.failed) setRecord(rec.v);
    if (!scr.failed) setScripts(scr.v);
    if (!raws.failed) setRawChapters(raws.v);
    setLoadError(rec.failed || scr.failed || raws.failed);
    setLoading(false);
    return rec.v;
  }, [storyId]);

  const reloadScripts = useCallback(async () => {
    const asked = storyId;
    const scr = await adaptationApi.scripts(storyId).catch(() => null);
    if (scr && currentStory.current === asked) setScripts(scr);
  }, [storyId]);

  useEffect(() => {
    setRecord(null); setScripts([]); setRawChapters([]); setLoading(true); setLoadError(false);
    reload();
  }, [reload]);

  return { record, scripts, rawChapters, loading, loadError, reload, reloadScripts };
};
